/**
 * A object representing the upload options returned from our backend
 *
 * @typedef UploadOptions
 * @property {string} policy
 * @property {string} signature
 * @property {string} api_key
 * @property {string} container
 * @property {string} path
 * @property {Array<string>} services
 * @property {Array<string>} extensions
 * @property {number} max_size
 * @property {[number, number]} image_max_dimensions
 * @property {string} region
 */

/**
 * Initializes a new instance of the Filestack client with the provided upload
 * options
 *
 * @param {() => Promise<UploadOptions>} uploadOptionsLoader An async function
 * that resolves with the upload options
 * @returns {Promise<{
 *   client: ReturnType<Filestack['init']>,
 *   uploadOptions: UploadOptions,
 *   storeOptions: {
 *     location: string,
 *     container: string,
 *     region: string,
 *     path: string
 *   }
 * }>} A Promise that resolves with an object with two keys:
 *   - client: an instance of the Filestack client
 *   - uploadOptions: the upload options returned from the upload options loader
 *   - storeOptions: the default storage options that can be passed to the
 *     client
 *
 * @example
 * const { client, uploadOptions, storeOptions } = await initFilestackClient(
 *   () => request.get(routes.IMAGES_UPLOAD_OPTIONS),
 * );
 */
export async function initFilestackClient(uploadOptionsLoader) {
  const uploadOptions = await uploadOptionsLoader();
  const Filestack = await import(
    /* webpackChunkName: "vendor-filestack" */ 'filestack-js'
  );

  const client = Filestack.init(uploadOptions.api_key, {
    security: {
      policy: uploadOptions.policy,
      signature: uploadOptions.signature,
    },
  });

  const storeOptions = {
    location: 's3',
    container: uploadOptions.container,
    region: uploadOptions.region,
    path: uploadOptions.path,
  };

  return {
    client,
    uploadOptions,
    storeOptions,
  };
}
